import React, { useState, useEffect, useRef } from 'react';
import './styles/App.css';
import Login from './components/Login';
import Header from './components/Header';
import Message from './components/Message';
import UserManagement from './components/UserManagement';
import ErrorBoundary from './components/ErrorBoundary';
import ConversationHistory from './components/ConversationHistory';
import { apiService } from './services/api';
import { conversationService } from './services/conversationService';
import config from './config';
import MicButton from './components/MicButton';
import './styles/MicButton.css';
import EmailComposer from './components/EmailComposer';     // ← NEW
import TrackerPage from './components/TrackerPage';           // ← NEW
import HomeDashboard from './components/HomeDashboard';       // ← NEW: Daily KPI dashboard
import SkillsManager from './components/SkillsManager';       // ← SKILLS
import SkillSelector from './components/SkillSelector';       // ← SKILLS
import useSkills from './hooks/useSkills';                    // ← SKILLS
import useCortexStream from './hooks/useCortexStream';        // ◀── STREAMING

// ════════════════════════════════════════════════════════════
// CORTEX LOADING INDICATOR
// ════════════════════════════════════════════════════════════

const CORTEX_LOADING_STAGES = [
  { label: 'Planning the next steps', icon: '🧠', delayMs: 0 },
  { label: 'Choosing data sources to use', icon: '📊', delayMs: 3000 },
  { label: 'Getting additional context', icon: '🔍', delayMs: 7000 },
  { label: 'Generating SQL query', icon: '⚙️', delayMs: 12000 },
  { label: 'Executing query on database', icon: '🗄️', delayMs: 18000 },
  { label: 'Reviewing the results', icon: '📋', delayMs: 28000 },
  { label: 'Analyzing data patterns', icon: '📈', delayMs: 40000 },
  { label: 'Forming the answer', icon: '✍️', delayMs: 55000 },
];

const CortexLoadingIndicator = () => {
  const [visibleStages, setVisibleStages] = useState([0]);

  useEffect(() => {
    const timers = [];
    CORTEX_LOADING_STAGES.forEach((stage, idx) => {
      if (idx === 0) return;
      const timer = setTimeout(() => {
        setVisibleStages(prev => [...prev, idx]);
      }, stage.delayMs);
      timers.push(timer);
    });
    return () => timers.forEach(t => clearTimeout(t));
  }, []);

  const latestStage = CORTEX_LOADING_STAGES[visibleStages[visibleStages.length - 1]];

  return (
    <div className="message assistant-message">
      <div className="message-avatar">🤖</div>
      <div className="message-content">
        <div className="cortex-loading">
          {visibleStages.length > 1 && (
            <div className="cortex-loading__completed">
              {visibleStages.slice(0, -1).map((stageIdx) => {
                const stage = CORTEX_LOADING_STAGES[stageIdx];
                return (
                  <div key={stageIdx} className="cortex-loading__step cortex-loading__step--done">
                    <span className="cortex-loading__check">✓</span>
                    <span className="cortex-loading__label">{stage.label}</span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="cortex-loading__step cortex-loading__step--active">
            <div className="cortex-loading__spinner" />
            <span className="cortex-loading__label">{latestStage.label}...</span>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queriesLeft, setQueriesLeft] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [quickQuestions, setQuickQuestions] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [queryLoading, setQueryLoading] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversationHistoryKey, setConversationHistoryKey] = useState(0);

  // ─────────────────────────────────────────────────────────
  // Engine state: controls which loader to show (Cortex vs legacy dots).
  // Kept in sync with apiService via updateQueryEngine().
  // ─────────────────────────────────────────────────────────
  const [activeEngine, setActiveEngine] = useState('legacy');

  // ─── Thread limit + retry state ───
  const [threadMessageCount, setThreadMessageCount] = useState(0);
  const [threadLimits, setThreadLimits] = useState({ warn_at: 999, max_messages: 999 });
  const [lastFailedQuestion, setLastFailedQuestion] = useState(null);
  const [showWarningBanner, setShowWarningBanner] = useState(false);
  const warningTimerRef = useRef(null);

  // ─── NEW: Email Share + Tracker state ───
  const [showTracker, setShowTracker] = useState(false);
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const [emailComposerData, setEmailComposerData] = useState(null);

  // ─── NEW: Home Dashboard state (shown instead of welcome msg if user has access) ───
  const [showDashboard, setShowDashboard] = useState(true);

  // ─── SKILLS: Skills Manager page + skill hook ───
  const [showSkillsManager, setShowSkillsManager] = useState(false);
  const {
    skills,
    selectedSkillId,
    setSelectedSkillId,
    getEffectiveSkillId,
    resetSelection: resetSkillSelection,
    fetchSkills,
  } = useSkills();

  // ◀── SKILLS: Derive access flag from currentUser (set by login/fetchUserInfo)
  const hasSkillAccess = currentUser?.skill_access === 'Yes' || currentUser?.skill_access === 'YES';

  // ◀── STREAMING: Hook for real-time Cortex SSE streaming
  const [streamingEnabled, setStreamingEnabled] = useState(false);
  const {
    streamQuery,
    isStreaming,
    streamingMessage,
    abortStream,
    setStreamingMessage,
  } = useCortexStream(config.apiBaseUrl);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const currentConversationIdRef = useRef(null);
  const voiceQuestionIdRef = useRef(null);

  /**
   * Single function to update engine in both places:
   * 1. apiService.queryEngine (for API request body logic)
   * 2. React state activeEngine (for loader UI decision)
   */
  const updateQueryEngine = (engine) => {
    const resolved = engine || 'legacy';
    // TODO: Remove this debug log after confirming the fix works
    console.log('[ENGINE] updateQueryEngine:', resolved);
    apiService.setQueryEngine(resolved);
    setActiveEngine(resolved);
  };

  // ─── NEW: Share handler — opens EmailComposer modal ───
  const handleShareMessage = (messageData) => {
    setEmailComposerData(messageData);
    setEmailComposerOpen(true);
  };

  useEffect(() => { currentConversationIdRef.current = currentConversationId; }, [currentConversationId]);
  useEffect(() => {
    const checkMobile = () => setSidebarCollapsed(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ◀── STREAMING: Check if backend has streaming enabled (once on mount)
  useEffect(() => {
    const checkStreaming = async () => {
      try {
        const r = await fetch(`${config.apiBaseUrl}/api/config/streaming`);
        if (r.ok) {
          const data = await r.json();
          setStreamingEnabled(data.streaming_enabled === true);
          console.log('[STREAMING] Backend streaming:', data.streaming_enabled);
        }
      } catch (e) {
        console.warn('[STREAMING] Could not check streaming config:', e);
        setStreamingEnabled(false);
      }
    };
    checkStreaming();
  }, []);

  // ◀── STREAMING: Auto-scroll when streaming message updates
  useEffect(() => {
    if (isStreaming && streamingMessage) {
      scrollToBottom();
    }
  }, [isStreaming, streamingMessage]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { checkAuthStatus(); }, []);
  // When a permanent message lands (array changed), use smooth scroll.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { smoothScrollToBottom(); }, [messages]);
  useEffect(() => {
    if (currentUser) {
      loadQuickQuestions();
      loadThreadLimits();
      setMessages([{
        id: Date.now(),
        content: `👋 Welcome ${currentUser.username}! I'm your VBL Sales AI Agent. You can select questions from the "Quick Questions" menu or type your own questions.`,
        isUser: false, timestamp: new Date()
      }]);
      setConversationHistoryKey(prev => prev + 1);
    }
  }, [currentUser]);

  const loadThreadLimits = async () => {
    try {
      const limits = await apiService.getThreadLimits();
      if (limits && limits.max_messages) {
        setThreadLimits({ warn_at: limits.warn_at || 10, max_messages: limits.max_messages || 15 });
      }
    } catch (e) { console.warn('Could not load thread limits:', e); }
  };

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem(config.storage.tokenKey);
      const userStr = localStorage.getItem(config.storage.userKey);
      if (token && userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user); setIsAuthenticated(true);
        setIsAdmin(user.is_admin === 'Yes' || user.is_admin === 'YES' || user.is_admin === true);
        setQueriesLeft(user.query_cap || 0);

        // Restore engine from persisted user object (set by Login.js or fetchUserInfo)
        updateQueryEngine(user.query_engine || 'legacy');

        // Then refresh from server (will update if changed)
        await fetchUserInfo(token);
      } else { setIsAuthenticated(false); setCurrentUser(null); }
    } catch (e) { console.error(e); setIsAuthenticated(false); setCurrentUser(null); }
    finally { setLoading(false); }
  };

  const fetchUserInfo = async (token) => {
    try {
      const r = await fetch(`${config.apiBaseUrl}/api/user-info`, {
        headers: { 'Authorization': `Bearer ${token || localStorage.getItem(config.storage.tokenKey)}`, 'Content-Type': 'application/json' }
      });
      if (r.ok) {
        const d = await r.json();
        // TODO: Remove this debug log after confirming the fix works
        console.log('[ENGINE] fetchUserInfo query_engine:', d.query_engine);
        setIsAdmin(d.is_admin === 'Yes' || d.is_admin === 'YES' || d.is_admin === true);
        setQueriesLeft(d.query_cap || 0);

        // Always update engine from server (authoritative)
        updateQueryEngine(d.query_engine || 'legacy');

        // Use functional update to avoid stale currentUser closure
        setCurrentUser(prev => {
          const merged = { ...(prev || {}), ...d };
          localStorage.setItem(config.storage.userKey, JSON.stringify(merged));
          return merged;
        });
      }
    } catch (e) { console.error(e); }
  };

  const handleLogin = (d) => {
    if (d.success && d.token && d.user) {
      // Merge query_engine into user object so it persists in localStorage.
      // Backend sends query_engine at top level of login response, not inside d.user.
      // Login.js also stores it, but we ensure it here as the final authority.
      const userWithEngine = {
        ...d.user,
        query_engine: d.query_engine || d.user.query_engine || 'legacy',
      };
      localStorage.setItem(config.storage.userKey, JSON.stringify(userWithEngine));
      setCurrentUser(userWithEngine);
      setIsAuthenticated(true);
      setIsAdmin(d.user.is_admin === 'Yes' || d.user.is_admin === 'YES' || d.user.is_admin === true);
      setQueriesLeft(d.user.query_cap || 0);
      updateQueryEngine(userWithEngine.query_engine);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(config.storage.tokenKey);
    localStorage.removeItem(config.storage.userKey);
    updateQueryEngine('legacy');
    abortStream();  // ◀── STREAMING: abort any in-flight stream on logout
    setIsAuthenticated(false); setCurrentUser(null); setShowUserManagement(false);
    setShowTracker(false);   // ← NEW: reset tracker on logout
    setShowSkillsManager(false);                          // ◀── SKILLS
    setIsAdmin(false); setQueriesLeft(0); setMessages([]); setCurrentConversationId(null);
    setConversationHistoryKey(0);
  };

  const loadQuickQuestions = async () => {
    try {
      const r = await apiService.getPredefinedQuestions();
      if (r?.questions) setQuickQuestions(r.questions.map(q => ({
        QUESTION_ID: q.question_id, QUESTION_TEXT: q.question_text,
        CATEGORY: q.category || 'General', DESCRIPTION: q.description
      })));
    } catch (e) { console.error(e); }
  };

  // ── Scroll helpers ──
  // The scroll container is .messages-container (overflow-y: auto), NOT
  // the document body. We track it with messagesContainerRef so that
  // isNearBottom and the scroll calls operate on the correct element.
  //
  // During streaming: instant ('auto') scroll — tokens arrive every
  // 40-80ms, faster than a smooth animation (~300ms) can complete.
  // Smooth scroll during streaming causes the viewport to judder.
  //
  // One-shot events (permanent message lands): smooth scroll is fine.
  //
  // Both respect a "stickiness threshold": if the user has scrolled up
  // more than 150px from the bottom, we leave them alone.
  const SCROLL_STICKY_PX = 150;
  const streamScrollPending = useRef(false);

  const isNearBottom = () => {
    const el = messagesContainerRef.current;
    if (!el) return true;  // no container yet → scroll
    return (el.scrollHeight - el.scrollTop - el.clientHeight) <= SCROLL_STICKY_PX;
  };

  const smoothScrollToBottom = () => {
    if (isNearBottom()) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Kept as `scrollToBottom` so existing call sites don't need renaming.
  // Rewired: instant scroll, rAF-coalesced, stickiness-aware.
  const scrollToBottom = () => {
    if (streamScrollPending.current) return;
    streamScrollPending.current = true;
    requestAnimationFrame(() => {
      streamScrollPending.current = false;
      if (isNearBottom()) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      }
    });
  };
  const truncateTitle = (t, n = 50) => {
    if (!t) return 'Voice conversation';
    const c = t.replace(/\s+/g, ' ').trim();
    return c.length <= n ? c : c.substring(0, n).trim() + '...';
  };

  const handleNewConversation = () => {
    setShowUserManagement(false);
    setShowTracker(false);
    setShowSkillsManager(false);                          // ◀── SKILLS
    setShowDashboard(false);  // New Chat = fresh chat, NOT dashboard
    setCurrentConversationId(null);
    apiService.resetCortexThread();
    abortStream();                                        // ◀── STREAMING: abort any in-flight stream
    setStreamingMessage(null);                             // ◀── STREAMING: clear streaming state
    resetSkillSelection();                                // ◀── SKILLS: reset skill selection for new thread
    setThreadMessageCount(0);
    setLastFailedQuestion(null);
    setShowWarningBanner(false);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    setMessages([{ id: Date.now(), content: `👋 Welcome ${currentUser?.username}! I'm your VBL Sales AI Agent. You can select questions from the "Quick Questions" menu or type your own questions.`, isUser: false, timestamp: new Date() }]);
    setConversationHistoryKey(p => p + 1);
  };

  // ← NEW: Navigate back to dashboard (logo click)
  const handleShowDashboard = () => {
    setShowUserManagement(false);
    setShowTracker(false);
    setShowSkillsManager(false);                          // ◀── SKILLS
    setShowDashboard(true);
    setCurrentConversationId(null);
    apiService.resetCortexThread();
    abortStream();                                        // ◀── STREAMING: abort any in-flight stream
    setStreamingMessage(null);                             // ◀── STREAMING: clear streaming state
    resetSkillSelection();                                // ◀── SKILLS
    setThreadMessageCount(0);
    setLastFailedQuestion(null);
    setShowWarningBanner(false);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    setMessages([{ id: Date.now(), content: `👋 Welcome ${currentUser?.username}!`, isUser: false, timestamp: new Date() }]);
    setConversationHistoryKey(p => p + 1);
  };

  const handleConversationSelect = async (conversation) => {
    try {
      setShowUserManagement(false);
      setShowTracker(false);   // ← NEW
      setShowSkillsManager(false);                        // ◀── SKILLS
      setShowDashboard(false); // ← NEW: hide dashboard when viewing a conversation
      setCurrentConversationId(conversation.conversation_id);
      apiService.resetCortexThread();
      abortStream();                                      // ◀── STREAMING: abort any in-flight stream
      setStreamingMessage(null);                           // ◀── STREAMING: clear streaming state
      resetSkillSelection();                              // ◀── SKILLS

      // ── Restore Cortex thread state from conversation ──
      // The backend now persists cortex_thread_id and last_assistant_message_id
      // at the conversation level. If present, restore them so the next
      // question in this thread carries the correct IDs to Snowflake Cortex,
      // preserving conversational context. For pre-migration conversations
      // that lack these fields, fall back to extracting from the last
      // message's metadata; if neither exists, the backend will auto-create
      // a new Cortex thread (graceful degradation).
      const savedThreadId = conversation.cortex_thread_id;
      const savedMsgId = conversation.last_assistant_message_id;
      if (savedThreadId) {
        apiService.setCortexThreadState({
          cortex_thread_id: savedThreadId,
          parent_message_id: savedMsgId ?? 0,
        });
      } else if (conversation.messages?.length > 0) {
        // Backward compatibility: extract from last message metadata
        const lastMsg = conversation.messages[conversation.messages.length - 1];
        const lastMeta = lastMsg?.metadata || {};
        if (lastMeta.cortex_thread_id) {
          apiService.setCortexThreadState({
            cortex_thread_id: lastMeta.cortex_thread_id,
            parent_message_id: lastMeta.assistant_message_id ?? lastMeta.parent_message_id ?? 0,
          });
        }
      }
      const msgs = [];
      if (conversation.messages?.length > 0) {
        conversation.messages.forEach((m) => {
          msgs.push({ id: Date.now() + Math.random(), content: m.question, isUser: true, timestamp: new Date(m.created_at) });

          const a = {
            id: Date.now() + Math.random() + 1,
            content: m.response,
            isUser: false,
            timestamp: new Date(m.created_at),
            onShare: handleShareMessage,   // ← NEW
            _originalQuestion: m.question, // ← NEW: for EmailComposer subject
          };

          // ── Rehydrate table data ──
          // Backend stores rows under `data` (current) with `data_preview`
          // kept as a fallback for pre-migration records. Columns are stored
          // alongside; if absent on older records, derive from the first row.
          const historicalData = m.data || m.data_preview || null;
          if (historicalData && historicalData.length > 0) {
            a.data = historicalData;
            a.columns = (m.columns && m.columns.length > 0)
              ? m.columns
              : (m.metadata?.columns || Object.keys(historicalData[0] || {}));
          }

          // Determine if this was a Cortex message
          const isCortexMsg = m.metadata?.cortex_thread_id || m.metadata?.has_thinking || activeEngine === 'cortex';
          if (isCortexMsg) {
            a.queryEngine = 'cortex';
            // ── Rehydrate charts ──
            // Charts are now persisted to MongoDB (m.charts). Older records
            // without charts will simply show an empty array — still renders
            // correctly, just without the chart panel.
            a.cortexData = {
              thinking_steps: [],   // Not persisted — collapses to no-op in UI
              charts: m.charts || [],
              status_steps: [],     // Not persisted — collapses to no-op in UI
            };
          }

          msgs.push(a);
        });
      }
      setMessages(msgs);
      setThreadMessageCount(conversation.messages?.length || 0);
      if (window.innerWidth <= 768) setSidebarCollapsed(true);
    } catch (e) { console.error(e); }
  };

  const buildAssistantMessage = (r, overrides = {}) => {
    const am = {
      id: Date.now() + 1,
      content: r.response || 'Done.',
      isUser: false,
      timestamp: new Date(),
      onShare: handleShareMessage,       // ← NEW
      _originalQuestion: r._question,    // ← NEW (set before calling)
      ...overrides,
    };
    if (r.show_data && r.data?.length > 0) {
      am.data = r.data;
      am.columns = r.columns;
    }
    if (r.chart_config) {
      am.chartConfig = r.chart_config;
    }
    if (r.query_engine === 'cortex') {
      am.queryEngine = 'cortex';
      if (r.cortex_data) {
        am.cortexData = r.cortex_data;
      }
    }
    return am;
  };

  // ══════════════════════════════════════════════════════════════
  // STREAMING sendMessage — uses SSE when streaming is enabled
  // Falls back to existing blocking flow when streaming is off
  // ══════════════════════════════════════════════════════════════

  const sendMessage = async (questionText = null) => {
    const msg = questionText || inputValue.trim();
    if (!msg || queryLoading) return;
    if (showUserManagement) setShowUserManagement(false);
    if (showTracker) setShowTracker(false);   // ← NEW
    if (showSkillsManager) setShowSkillsManager(false);   // ◀── SKILLS
    if (showDashboard) setShowDashboard(false);  // ← NEW: hide dashboard when chatting

    // Thread limit check
    if (threadMessageCount >= threadLimits.max_messages) {
      setMessages(p => [...p, {
        id: Date.now() + 1,
        content: `🚫 This conversation has reached its limit of ${threadLimits.max_messages} questions. Please start a new chat to continue.`,
        isUser: false, timestamp: new Date()
      }]);
      return;
    }

    console.log('[LOADER] Sending query. activeEngine:', activeEngine, 'streaming:', streamingEnabled && activeEngine === 'cortex');

    setMessages(p => [...p, { id: Date.now(), content: msg, isUser: true, timestamp: new Date() }]);
    setInputValue(''); setQueryLoading(true);
    setLastFailedQuestion(null);

    try {
      // ── Create conversation if needed ──
      let cid = currentConversationIdRef.current;
      if (!cid) {
        try {
          const cr = await conversationService.createConversation(truncateTitle(msg));
          if (cr.success && cr.conversation_id) { cid = cr.conversation_id; setCurrentConversationId(cid); }
        } catch (e) { console.error(e); }
      }

      // ══════════════════════════════════════════════════════
      // ◀── STREAMING PATH: Use SSE for real-time Cortex responses
      // ══════════════════════════════════════════════════════
      if (streamingEnabled && activeEngine === 'cortex') {
        console.log('[STREAMING] Using SSE stream for query');

        const token = localStorage.getItem(config.storage.tokenKey);
        const cortexState = apiService.getCortexThreadState();

        const result = await streamQuery({
          question: msg,
          conversation_id: cid || '',
          cortex_thread_id: cortexState.cortex_thread_id || '',
          parent_message_id: cortexState.parent_message_id || 0,
          token: token,
          skill_id: getEffectiveSkillId() || '',
        });

        // ── Handle result ──
        console.log('[STREAMING] Stream result:', JSON.stringify({
          hasResult: !!result,
          hasError: result?.error,
          response: result?.response?.substring?.(0, 100),
          response_text: result?.response_text?.substring?.(0, 100),
          hasCortexData: !!result?.cortex_data,
          thinkingCount: result?.cortex_data?.thinking_steps?.length ?? result?.thinking_steps?.length ?? 0,
          chartsCount: result?.cortex_data?.charts?.length ?? result?.charts?.length ?? 0,
          statusCount: result?.cortex_data?.status_steps?.length ?? result?.status_steps?.length ?? 0,
          dataRows: result?.data?.length ?? 0,
          columnsCount: result?.columns?.length ?? 0,
        }));
        if (result?.error) {
          if (result.thread_limit_reached) {
            setMessages(p => [...p, {
              id: Date.now() + 1,
              content: `🚫 ${result.error || 'Thread limit reached. Please start a new conversation.'}`,
              isUser: false, timestamp: new Date()
            }]);
            setThreadMessageCount(threadLimits.max_messages);
          } else {
            setMessages(p => [...p, {
              id: Date.now() + 1,
              content: `❌ ${result.error || result.message || 'An error occurred.'}`,
              isUser: false, timestamp: new Date()
            }]);
          }
          if (result.query_cap !== undefined) {
            setQueriesLeft(result.query_cap);
          }
        } else if (result) {
          // Update Cortex thread state for next message in this conversation
          if (result.cortex_thread_id) {
            apiService.setCortexThreadState({
              cortex_thread_id: result.cortex_thread_id,
              parent_message_id: result.parent_message_id || 0,
            });
          }

          // Update query cap
          if (result.query_cap !== undefined) {
            setQueriesLeft(result.query_cap);
            localStorage.setItem('query_cap', String(result.query_cap));
          }

          // Build final assistant message from streaming result.
          // The done event normally arrives enriched by cortex_streaming.py
          // with 'response' and 'cortex_data'. But if enrichment fails
          // (JSON parse error, chunk boundary issue), the raw cortex_agent
          // format arrives instead — it uses 'response_text' and has flat
          // fields (thinking_steps, charts, status_steps) without a wrapper.
          // We handle both formats defensively.
          const responseText = result.response
            || result.response_text
            || '';
          const cortexData = result.cortex_data || {
            thinking_steps: result.thinking_steps || [],
            charts: result.charts || [],
            status_steps: result.status_steps || [],
          };

          const am = {
            id: Date.now() + 1,
            content: responseText,
            isUser: false,
            timestamp: new Date(),
            queryEngine: 'cortex',
            cortexData: cortexData,
            onShare: handleShareMessage,
            _originalQuestion: msg,
          };

          // Attach table data if available
          const tableData = result.data || [];
          const tableCols = result.columns || [];
          if (tableData.length > 0 && tableCols.length > 0) {
            am.data = tableData;
            am.columns = tableCols;
          }

          setMessages(p => [...p, am]);
          setStreamingMessage(null);  // Clear streaming state
          setConversationHistoryKey(p => p + 1);

          setThreadMessageCount(prev => {
            const newCount = prev + 1;
            if (newCount >= threadLimits.warn_at && newCount < threadLimits.max_messages) {
              setShowWarningBanner(true);
              if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
              warningTimerRef.current = setTimeout(() => setShowWarningBanner(false), 5000);
            }
            return newCount;
          });
        }
        loadQuickQuestions();

      } else {
        // ══════════════════════════════════════════════════════
        // EXISTING (non-streaming) PATH — unchanged
        // ══════════════════════════════════════════════════════
        const r = await apiService.sendQuery(msg, cid, getEffectiveSkillId());
        if (r.query_cap !== undefined) { setQueriesLeft(r.query_cap); localStorage.setItem('query_cap', String(r.query_cap)); }
        if (r.thread_limit_reached) {
          setMessages(p => [...p, { id: Date.now() + 1, content: `🚫 ${r.error || 'Thread limit reached. Please start a new conversation.'}`, isUser: false, timestamp: new Date() }]);
          setThreadMessageCount(threadLimits.max_messages);
        } else if (r.error) {
          setMessages(p => [...p, { id: Date.now() + 1, content: `❌ ${r.error}`, isUser: false, timestamp: new Date() }]);
        } else {
          r._question = msg;   // ← NEW: attach original question for share
          const am = buildAssistantMessage(r);
          setMessages(p => [...p, am]);
          setConversationHistoryKey(p => p + 1);
          setThreadMessageCount(prev => {
            const newCount = prev + 1;
            // Show warning banner briefly when in warning zone
            if (newCount >= threadLimits.warn_at && newCount < threadLimits.max_messages) {
              setShowWarningBanner(true);
              if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
              warningTimerRef.current = setTimeout(() => setShowWarningBanner(false), 5000);
            }
            return newCount;
          });
        }
        loadQuickQuestions();
      }
    } catch (e) {
      console.error(e);
      setLastFailedQuestion(msg);
      setMessages(p => [...p, { id: Date.now() + 1, content: `❌ ${e.message}`, isUser: false, timestamp: new Date(), isRetryable: true }]);
    } finally {
      setQueryLoading(false);
      setStreamingMessage(null);  // ◀── STREAMING: ensure streaming state is cleared
    }
  };

  const retryLastMessage = () => {
    if (!lastFailedQuestion) return;
    const q = lastFailedQuestion;
    setLastFailedQuestion(null);
    // Remove error message and the user question before it
    setMessages(p => {
      const copy = [...p];
      if (copy.length >= 2 && copy[copy.length - 1].isRetryable) {
        copy.pop();
        if (copy[copy.length - 1]?.isUser) copy.pop();
      }
      return copy;
    });
    sendMessage(q);
  };

  const sendVoiceQuery = async (questionText) => {
    if (!questionText?.trim()) return null;
    try {
      let cid = currentConversationIdRef.current;
      if (!cid) {
        try {
          const cr = await conversationService.createConversation(truncateTitle(questionText));
          if (cr.success && cr.conversation_id) { cid = cr.conversation_id; setCurrentConversationId(cid); }
        } catch (e) { console.error(e); }
      }
      const r = await apiService.sendQuery(questionText, cid, getEffectiveSkillId());
      if (r?.query_cap !== undefined) { setQueriesLeft(r.query_cap); localStorage.setItem('query_cap', String(r.query_cap)); }
      setConversationHistoryKey(p => p + 1);
      if (r.error) return { responseText: `Error: ${r.error}`, fullResponse: r };
      return { responseText: r.response || 'Query processed.', fullResponse: r };
    } catch (e) {
      console.error('[Voice] Query error:', e);
      return { responseText: `Sorry, error: ${e.message}`, fullResponse: null };
    }
  };

  const handleVoiceExchange = ({ question, answer, backendResponse, phase }) => {
    if (!question?.trim()) return;
    const now = Date.now();

    if (phase === 'question_only') {
      voiceQuestionIdRef.current = now;
      setMessages(p => [...p, {
        id: now, content: question, isUser: true, isVoice: true, timestamp: new Date(),
      }]);
      return;
    }

    if (phase === 'streaming') {
      setMessages(p => {
        const lastMsg = p.length > 0 ? p[p.length - 1] : null;
        if (lastMsg && !lastMsg.isUser && lastMsg.isVoice && lastMsg._streaming) {
          const updated = [...p];
          updated[updated.length - 1] = { ...lastMsg, content: answer };
          return updated;
        } else {
          return [...p, {
            id: now + 1, content: answer, isUser: false, isVoice: true,
            _streaming: true, timestamp: new Date(),
          }];
        }
      });
      return;
    }

    if (phase === 'full_response') {
      const am = {
        id: now + 1,
        content: answer || 'Response received.',
        isUser: false,
        isVoice: true,
        timestamp: new Date(),
        onShare: handleShareMessage,       // ← NEW
        _originalQuestion: question,       // ← NEW
      };
      if (backendResponse?.show_data && backendResponse?.data?.length > 0) {
        am.data = backendResponse.data;
        am.columns = backendResponse.columns;
      }
      if (backendResponse?.chart_config) {
        am.chartConfig = backendResponse.chart_config;
      }
      if (backendResponse?.query_engine === 'cortex') {
        am.queryEngine = 'cortex';
        if (backendResponse.cortex_data) {
          am.cortexData = backendResponse.cortex_data;
        }
      }
      if (backendResponse) {
        am.fullBackendData = {
          response: backendResponse.response,
          sql_query: backendResponse.sql_query,
          total_rows: backendResponse.total_rows,
        };
      }
      setMessages(p => {
        const lastMsg = p.length > 0 ? p[p.length - 1] : null;
        if (lastMsg && !lastMsg.isUser && lastMsg.isVoice && lastMsg._streaming) {
          const updated = [...p];
          updated[updated.length - 1] = am;
          return updated;
        }
        return [...p, am];
      });
      voiceQuestionIdRef.current = null;
      return;
    }

    // Legacy fallback
    const newMsgs = [
      { id: now, content: question, isUser: true, isVoice: true, timestamp: new Date() }
    ];
    if (answer?.trim()) {
      newMsgs.push({ id: now + 1, content: answer, isUser: false, isVoice: true, timestamp: new Date() });
    }
    setMessages(p => [...p, ...newMsgs]);
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };
  const toggleCategory = (c) => setExpandedCategory(expandedCategory === c ? null : c);

  const grouped = (quickQuestions || []).reduce((a, q) => {
    const c = q.CATEGORY || 'General'; if (!a[c]) a[c] = []; a[c].push(q); return a;
  }, {});
  const categories = Object.keys(grouped).sort();

  if (loading) return <div className="loading-container"><div className="loading-spinner"></div><p>Loading...</p></div>;
  if (!isAuthenticated) return <Login onLogin={handleLogin} />;

  return (
    <ErrorBoundary>
      <div className="app">
        <Header user={currentUser} queriesLeft={queriesLeft} isAdmin={isAdmin}
          onLogout={handleLogout} onOpenUserManagement={() => setShowUserManagement(!showUserManagement)}
          onOpenTracker={() => { setShowTracker(!showTracker); setShowUserManagement(false); setShowSkillsManager(false); }}
          onOpenSkills={() => { setShowSkillsManager(!showSkillsManager); setShowUserManagement(false); setShowTracker(false); }}
          showSkills={hasSkillAccess}
          onLogoClick={handleShowDashboard}
        />
        <div className="app-body">
          <div className={`collapsible-sidebar ${sidebarCollapsed ? 'collapsed' : 'expanded'}`}>
            <button className="sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
              {sidebarCollapsed ? '☰' : '✕'}
            </button>
            <div className="sidebar-content">
              <div className="sidebar-section quick-questions-section">
                <h3 className="section-title">💡 QUICK QUESTIONS</h3>
                <div className="section-content">
                  {!quickQuestions?.length ? <p className="no-questions">No questions available</p> : (
                    <div className="questions-accordion">
                      {categories.map(cat => (
                        <div key={cat} className="accordion-item">
                          <button className={`accordion-header ${expandedCategory === cat ? 'expanded' : ''}`} onClick={() => toggleCategory(cat)}>
                            <span className="accordion-title">{cat}</span>
                            <span className="accordion-icon">{expandedCategory === cat ? '▼' : '▶'}</span>
                          </button>
                          {expandedCategory === cat && (
                            <div className="accordion-content">
                              {grouped[cat].map(q => (
                                <button key={q.QUESTION_ID} className="question-button" onClick={() => sendMessage(q.QUESTION_TEXT)}
                                  title={q.DESCRIPTION} disabled={queryLoading}>
                                  <span className="question-icon">❓</span>
                                  <span className="question-text">{q.QUESTION_TEXT}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="sidebar-section conversation-history-wrapper">
                <ConversationHistory user={currentUser} onConversationSelect={handleConversationSelect}
                  onNewConversation={handleNewConversation} currentConversationId={currentConversationId}
                  isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
                  key={conversationHistoryKey} />
              </div>
            </div>
          </div>
          <div className={`app-main ${sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}>
            {showUserManagement ? (
              <UserManagement authToken={localStorage.getItem(config.storage.tokenKey)} />
            ) : showTracker ? (
              /* ── NEW: Tracker Page ── */
              <TrackerPage
                authToken={localStorage.getItem(config.storage.tokenKey)}
                onClose={() => setShowTracker(false)}
              />
            ) : showSkillsManager && hasSkillAccess ? (
              /* ── SKILLS: Skills Manager Page ── */
              <SkillsManager
                onClose={() => setShowSkillsManager(false)}
                onSkillsChanged={() => fetchSkills()}
              />
            ) : (
              <main className="chat-container">
                <div className="messages-container" ref={messagesContainerRef}>
                  {/* ── Home Dashboard (shown on landing, before any chat) ── */}
                  {showDashboard && !currentConversationId && (
                    <HomeDashboard
                      authToken={localStorage.getItem(config.storage.tokenKey)}
                      onNewChat={handleNewConversation}
                      onNoAccess={() => setShowDashboard(false)}
                    />
                  )}

                  {/* Messages — hidden when dashboard is active */}
                  {!(showDashboard && !currentConversationId) && messages.map(m => (
                    <React.Fragment key={m.id}>
                      <Message message={m} isUser={m.isUser} />
                      {m.isRetryable && lastFailedQuestion && !queryLoading && (
                        <div className="message assistant-message">
                          <div className="message-avatar" style={{ visibility: 'hidden' }}>🤖</div>
                          <div className="message-content">
                            <button onClick={retryLastMessage} style={{
                              display: 'inline-flex', alignItems: 'center', gap: '6px',
                              padding: '6px 16px', marginTop: '4px',
                              border: '1px solid #d0d5dd', borderRadius: '8px',
                              background: '#fff', color: '#344054', fontSize: '13px',
                              fontWeight: 500, cursor: 'pointer'
                            }}>
                              🔄 Retry this question
                            </button>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}

                  {/* ◀── STREAMING: Show live streaming message ── */}
                  {isStreaming && streamingMessage && (
                    <Message
                      message={{
                        ...streamingMessage,
                        id: 'streaming-live',
                        isUser: false,
                        timestamp: new Date(),
                      }}
                      isUser={false}
                    />
                  )}

                  {/* Loader: Cortex progressive steps OR legacy three-dots */}
                  {/* ◀── STREAMING: Hide old loader when streaming is active (streaming shows its own UI) */}
                  {queryLoading && !isStreaming && (
                    activeEngine === 'cortex' ? (
                      <CortexLoadingIndicator />
                    ) : (
                      <div className="typing-indicator">
                        <div className="message assistant-message">
                          <div className="message-avatar">🤖</div>
                          <div className="message-content"><div className="typing-dots"><span></span><span></span><span></span></div></div>
                        </div>
                      </div>
                    )
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Thread limit warning banner — auto-hides after 5s, always shows when blocked */}
                {(threadMessageCount >= threadLimits.max_messages || showWarningBanner) && (
                  <div style={{
                    padding: '8px 1.5rem', flexShrink: 0,
                    transition: 'opacity 0.3s ease',
                    opacity: (threadMessageCount >= threadLimits.max_messages || showWarningBanner) ? 1 : 0,
                  }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
                      maxWidth: '900px', margin: '0 auto',
                      background: threadMessageCount >= threadLimits.max_messages ? '#fef3f2' : '#fffaeb',
                      border: `1px solid ${threadMessageCount >= threadLimits.max_messages ? '#fda29b' : '#fec84b'}`,
                      color: threadMessageCount >= threadLimits.max_messages ? '#912018' : '#93370d',
                    }}>
                      <span>{threadMessageCount >= threadLimits.max_messages ? '🚫' : '⚠️'}</span>
                      <span style={{ flex: 1 }}>
                        {threadMessageCount >= threadLimits.max_messages
                          ? `This conversation has reached its limit of ${threadLimits.max_messages} questions. Please start a new chat.`
                          : `You've asked ${threadMessageCount} of ${threadLimits.max_messages} questions in this thread. ${threadLimits.max_messages - threadMessageCount} remaining.`
                        }
                      </span>
                      <button onClick={handleNewConversation} style={{
                        padding: '4px 12px', border: '1px solid #d0d5dd', borderRadius: '6px',
                        background: '#fff', color: '#344054', fontSize: '12px',
                        fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap'
                      }}>
                        ➕ New Chat
                      </button>
                    </div>
                  </div>
                )}

                <div className="input-container">
                  {/* ── SKILLS: Skill selector — only shown when user has skill_access ── */}
                  {hasSkillAccess && (
                  <div className="input-wrapper" style={{ flexWrap: 'wrap', paddingBottom: 0 }}>
                    <div style={{ width: '100%', marginBottom: '4px' }}>
                      <SkillSelector
                        skills={skills}
                        selectedSkillId={selectedSkillId}
                        onSelectSkill={(id) => {
                          if (currentConversationId && id !== getEffectiveSkillId()) {
                            handleNewConversation();
                            setTimeout(() => setSelectedSkillId(id), 50);
                            return;
                          }
                          setSelectedSkillId(id);
                        }}
                        onOpenSkillsManager={() => {
                          setShowSkillsManager(true);
                          setShowUserManagement(false);
                          setShowTracker(false);
                        }}
                        disabled={queryLoading || threadMessageCount >= threadLimits.max_messages}
                      />
                    </div>
                  </div>
                  )}
                  <div className="input-wrapper" style={{ paddingTop: 0 }}>
                    <textarea value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress}
                      placeholder={
                        threadMessageCount >= threadLimits.max_messages ? "Thread limit reached. Start a new chat."
                        : queriesLeft <= 0 ? "Query limit exhausted."
                        : "Type your question..."
                      }
                      disabled={queryLoading || queriesLeft <= 0 || threadMessageCount >= threadLimits.max_messages} rows={1} className="message-input" />
                    <MicButton sendQueryFn={sendVoiceQuery} onVoiceExchange={handleVoiceExchange}
                      disabled={queryLoading || queriesLeft <= 0 || threadMessageCount >= threadLimits.max_messages} />
                    <button onClick={() => sendMessage()} disabled={!inputValue.trim() || queryLoading || queriesLeft <= 0 || threadMessageCount >= threadLimits.max_messages}
                      className="send-btn" title={queriesLeft <= 0 ? "Limit exhausted" : "Send"}>
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                    </button>
                  </div>
                </div>
              </main>
            )}
          </div>
        </div>

        {/* ── NEW: Email Composer Modal (renders on top of everything) ── */}
        <EmailComposer
          isOpen={emailComposerOpen}
          onClose={() => { setEmailComposerOpen(false); setEmailComposerData(null); }}
          messageData={emailComposerData}
          authToken={localStorage.getItem(config.storage.tokenKey)}
          userEmail={currentUser?.email || currentUser?.username || ''}
        />
      </div>
    </ErrorBoundary>
  );
}

export default App;
